import React from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { DynamicsChartFrame } from './DynamicsChartFrame';
import { DYNAMICS_CARD_INSIGHTS } from './dynamicsInsights';
import { DYNAMICS_RECHARTS_TOOLTIP_COMMON } from './dynamicsRechartsTooltip';
import { useDynamicsPlotHeight } from './DynamicsChartLayoutContext';
import { ResolvedPeriod } from '../../PeriodPicker';
import { dynamicsBucketChartMargins, dynamicsPeriodAxisProps } from './dynamicsPeriodAxis';

interface Props {
  groupBy: 'sprint' | 'release' | 'week' | 'month';
  lastN: number;
  resolvedPeriod: ResolvedPeriod | null;
  onDataStatus?: (s: { metric: string; hasData: boolean; loading: boolean }) => void;
}

function PredictabilityChartBody({
  points,
  onPointClick,
}: {
  points: Array<Record<string, number | string | null>>;
  onPointClick?: (bucketLabel: string) => void;
}) {
  const plotH = useDynamicsPlotHeight(280);
  return (
    <ResponsiveContainer width="100%" height={plotH}>
      <LineChart
        data={points}
        margin={{ ...dynamicsBucketChartMargins }}
        style={onPointClick ? { cursor: 'pointer' } : undefined}
        onClick={(e) => {
          const label = (e as { activeLabel?: string } | null | undefined)?.activeLabel;
          if (onPointClick && typeof label === 'string') onPointClick(label);
        }}
      >
        <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.3} />
        <XAxis {...dynamicsPeriodAxisProps} />
        <YAxis unit="%" domain={[0, 120]} />
        <Tooltip {...DYNAMICS_RECHARTS_TOOLTIP_COMMON} />
        <ReferenceLine y={80} stroke="#22c55e" strokeDasharray="3 3" />
        <Line
          type="monotone"
          dataKey="doneOverPlanPct"
          name="Done ÷ Plan"
          stroke="#0ea5e9"
          strokeWidth={2}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

export const DynamicsI_SprintPredictability: React.FC<Props> = ({
  groupBy,
  lastN,
  resolvedPeriod,
  onDataStatus,
}) => {
  return (
    <DynamicsChartFrame
      id="I"
      title="Sprint predictability"
      hint="Done ÷ Plan % per sprint, with a reference line at 80%. Helps the team and stakeholders converge on realistic commitments."
      needs="Default board + scoped sprints. If your team doesn't commit to numbers per sprint, consider disabling this card."
      metric="predictability"
      groupBy={groupBy}
      lastN={lastN}
      resolvedPeriod={resolvedPeriod}
      onDataStatus={onDataStatus}
      chartHeight={280}
      insightsTitle={DYNAMICS_CARD_INSIGHTS.I.title}
      insights={[...DYNAMICS_CARD_INSIGHTS.I.bullets]}
    >
      {(points, onPointClick) => (
        <PredictabilityChartBody points={points} onPointClick={onPointClick} />
      )}
    </DynamicsChartFrame>
  );
};
